import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../components-v2/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../components-v2/Popover';
import { TabList, TabRoot, TabTrigger } from '../../components-v2/Tabs';
import { SPACE, THEME } from '../../components-v2/Theme/theme';
import { FolderIcon } from '../../components/svgs/icons/FolderIcon';
import { FolderInfo } from './folderUtils';

const styleSheet = StyleSheet.create({
	container: {
		alignItems: 'flex-end',
		display: 'flex',
		gap: SPACE[16],
		justifyContent: 'flex-end',
		marginBottom: SPACE[12],
	},
	tabs: {
		flex: 1,
		minWidth: 0,
	},
	hint: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: '20px',
		margin: 0,
		maxWidth: 280,
	},
});

interface TemplateFolderTabsProps {
	folders: FolderInfo[];
	selectedFolder: string | null;
	onFolderSelect: (folder: string | null) => void;
}

/**
 * Horizontal tab bar for filtering templates by folder.
 * Renders an "All" tab followed by folder tabs in alphabetical order.
 * Reuses CampaignBrowserTabs styling for consistency.
 */
export const TemplateFolderTabs: React.FC<TemplateFolderTabsProps> = ({ folders, selectedFolder, onFolderSelect }) => {
	return (
		<div className={css(styleSheet.container)}>
			{folders.length > 0 && (
				<div className={css(styleSheet.tabs)}>
					<TabRoot value={selectedFolder} onValueChange={onFolderSelect}>
						<TabList>
							<TabTrigger value={null}>All</TabTrigger>
							{folders.map(folder => (
								<TabTrigger key={folder.name} value={folder.name}>
									{folder.name}
								</TabTrigger>
							))}
						</TabList>
					</TabRoot>
				</div>
			)}
			{/* Folders exist only as keywords on templates, so an empty folder has nowhere to live; point to the card. */}
			<Popover>
				<PopoverTrigger asChild>
					<Button variant='link'>
						<FolderIcon fillColor='currentColor' />
						Add Folder
					</Button>
				</PopoverTrigger>
				<PopoverContent align='end'>
					<p className={css(styleSheet.hint)}>
						To create a folder, find one of your templates and click <strong>Add to Folder</strong> on its card. Name
						the folder and it will appear here as a tab.
					</p>
				</PopoverContent>
			</Popover>
		</div>
	);
};
